import React, { useState, useEffect } from 'react';
import styles from './UserKycDetails.module.css';
import axios from 'axios';
import { REACT_APP_API_BASE_URL } from '../../../apiurl';
import { formatAdminDate } from '../../../utils/datetime';

const UserKycDetails = ({ userId }) => {
    const [kycData, setKycData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [verifyModalOpen, setVerifyModalOpen] = useState(false);
    const [selectedVerifyType, setSelectedVerifyType] = useState(null); // 'PAN' or 'AADHAR'

   


    useEffect(() => {
        fetchKycData();
    }, [userId]);

    const fetchKycData = async () => {
        if (!userId) {
            setLoading(false);
            return;
        }

        try {
            // Updated to use the correct endpoint structure as verified in backend analysis
            const response = await axios.get(`${REACT_APP_API_BASE_URL}api/userkyc/getuserkyc/${userId}`);

            if (response.data && response.data.success) {
                setKycData(response.data.data);
            } else {
                // If success is true but data is null (handled in backend)
                setKycData(null);
            }
        } catch (err) {
            console.error("Error fetching KYC data:", err);
            setError("Failed to load KYC details.");
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyFilter = async (type, status, source = 'Own') => {
        try {
            setLoading(true);
            const response = await axios.post(`${REACT_APP_API_BASE_URL}api/userkyc/verify`, {
                userId,
                type,
                status,
                verifiedDisplay: source
            });
            if (response.data.success) {
                setVerifyModalOpen(false);
                setSelectedVerifyType(null);
                await fetchKycData();
            }
        } catch (error) {
            console.error("Verification failed", error);
            setError("Verification failed");
        } finally {
            setLoading(false);
        }
    };

    const openVerifyModal = (type) => {
        setSelectedVerifyType(type);
        setVerifyModalOpen(true);
    };

    if (loading) {
        return (
            <div className={styles.loadingContainer}>
                <div className={styles.spinner}></div>
                <p>Loading KYC Details...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className={styles.errorContainer}>
                <span className={styles.errorIcon}>⚠️</span>
                <p>{error}</p>
            </div>
        );
    }

    if (!kycData) {
        return (
            <div className={styles.emptyContainer}>
                <p>No KYC Details found for this user.</p>
            </div>
        );
    }

    const {
        pan_no,
        adhaar_no,
        date_of_birth,
        gender,
        father_name,
        address,
        city,
        state,
        pin_code,
        pan_verified,
        adhaar_verified,
        pan_doc,
        aadhar_doc,
        aadhar_back_doc
    } = kycData;

    const formatDate = (dateString) => formatAdminDate(dateString, 'Not Provided');

    const renderVerificationControls = (type, isVerified) => (
        <div className={styles.verificationControls}>
            <span className={isVerified ? styles.verifiedBadge : styles.pendingBadge}>
                {isVerified ? 'Verified' : 'Pending'}
            </span>
            <div className={styles.btnGroup}>
                {!isVerified && (
                    <button
                        className={styles.verifyBtn}
                        onClick={() => openVerifyModal(type)}
                    >
                        Verify
                    </button>
                )}

                {isVerified && (
                    <button
                        className={styles.rejectBtn}
                        onClick={() => handleVerifyFilter(type, false)}
                    >
                        Reject
                    </button>
                )}
            </div>
        </div>
    );

    const renderField = (label, value, isVerified = null, type = null) => (
        <div className={styles.fieldItem}>
            <span className={styles.label}>{label}</span>
            <div className={styles.valueContainer}>
                <span className={styles.value}>{value || 'Not Provided'}</span>
                {isVerified !== null && renderVerificationControls(type, isVerified)}
            </div>
        </div>
    );

    return (
        <div className={styles.kycContainer}>
            <div className={styles.header}>
                <h4>KYC Information</h4>
                <span className={styles.userId}>User ID: {userId}</span>
            </div>

            <div className={styles.gridContainer}>
                {/* Personal Details Section */}
                <div className={styles.section}>
                    <h5 className={styles.sectionTitle}>Personal Details</h5>
                    {renderField("Father's Name", father_name)}
                    {renderField("Date of Birth", formatDate(date_of_birth))}
                    {renderField("Gender", gender)}
                </div>

                {/* Identification Section */}
                <div className={styles.section}>
                    <h5 className={styles.sectionTitle}>Identification</h5>
                    {renderField("PAN Number", pan_no, pan_verified, 'PAN')}
                    {renderField("Aadhaar Number", adhaar_no, adhaar_verified, 'AADHAR')}
                </div>

                {/* Address Section */}
                <div className={styles.section}>
                    <h5 className={styles.sectionTitle}>Address Details</h5>
                    {renderField("Address", address)}
                    <div className={styles.row}>
                        {renderField("City", city)}
                        {renderField("State", state)}
                        {renderField("Pincode", pin_code)}
                    </div>
                </div>
            </div>

            {/* Documents Section */}
            {(pan_doc || aadhar_doc || aadhar_back_doc) && (
                <div className={styles.documentsSection}>
                    <h5 className={styles.sectionTitle}>Documents</h5>
                    <div className={styles.docGrid}>
                        {pan_doc && (
                            <div className={styles.docItem}>
                                <span className={styles.docLabel}>PAN Card</span>
                                <div className={styles.imagePreview}>
                                    <a
                                        href={pan_doc.startsWith('http') || pan_doc.startsWith('data:') ? pan_doc : `${REACT_APP_API_BASE_URL}${pan_doc}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        title="Click to view full size"
                                    >
                                        <img
                                            src={pan_doc.startsWith('http') || pan_doc.startsWith('data:') ? pan_doc : `${REACT_APP_API_BASE_URL}${pan_doc}`}
                                            alt="PAN Document"
                                            className={styles.docImage}
                                            onError={(e) => e.target.src = 'https://via.placeholder.com/300x200?text=Image+Error'}
                                        />
                                    </a>
                                </div>
                            </div>
                        )}
                        {aadhar_doc && (
                            <div className={styles.docItem}>
                                <span className={styles.docLabel}>Aadhaar Front</span>
                                <div className={styles.imagePreview}>
                                    <a
                                        href={aadhar_doc.startsWith('http') || aadhar_doc.startsWith('data:') ? aadhar_doc : `${REACT_APP_API_BASE_URL}${aadhar_doc}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        title="Click to view full size"
                                    >
                                        <img
                                            src={aadhar_doc.startsWith('http') || aadhar_doc.startsWith('data:') ? aadhar_doc : `${REACT_APP_API_BASE_URL}${aadhar_doc}`}
                                            alt="Aadhaar Front"
                                            className={styles.docImage}
                                            onError={(e) => e.target.src = 'https://via.placeholder.com/300x200?text=Image+Error'}
                                        />
                                    </a>
                                </div>
                            </div>
                        )}
                        {aadhar_back_doc && (
                            <div className={styles.docItem}>
                                <span className={styles.docLabel}>Aadhaar Back</span>
                                <div className={styles.imagePreview}>
                                    <a
                                        href={aadhar_back_doc.startsWith('http') || aadhar_back_doc.startsWith('data:') ? aadhar_back_doc : `${REACT_APP_API_BASE_URL}${aadhar_back_doc}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        title="Click to view full size"
                                    >
                                        <img
                                            src={aadhar_back_doc.startsWith('http') || aadhar_back_doc.startsWith('data:') ? aadhar_back_doc : `${REACT_APP_API_BASE_URL}${aadhar_back_doc}`}
                                            alt="Aadhaar Back"
                                            className={styles.docImage}
                                            onError={(e) => e.target.src = 'https://via.placeholder.com/300x200?text=Image+Error'}
                                        />
                                    </a>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Verification Modal */}
            {verifyModalOpen && (
                <div className={styles.modalOverlay}>
                    <div className={styles.modalContent}>
                        <div className={styles.modalHeader}>
                            <h5>Verify {selectedVerifyType === 'PAN' ? 'PAN Card' : 'Aadhaar Card'}</h5>
                            <button className={styles.closeBtn} onClick={() => setVerifyModalOpen(false)}>×</button>
                        </div>
                        <div className={styles.modalBody}>
                            <p>Select the verification method:</p>
                            <div className={styles.modalActions}>
                                <button
                                    className={styles.ownVerifyBtn}
                                    onClick={() => handleVerifyFilter(selectedVerifyType, true, 'Own')}
                                >
                                    Verified by InRiser (Own)
                                </button>
                                <button
                                    className={styles.govtVerifyBtn}
                                    onClick={() => handleVerifyFilter(selectedVerifyType, true, 'Government')}
                                >
                                    Verified by Govt Portal
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserKycDetails;
